import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Panel, PanelBody, PanelHead, Pill } from "../../components";

interface Step {
  title: string;
  body: ReactNode;
}

const STEPS: Step[] = [
  {
    title: "Load supplier prices",
    body: (
      <>
        <Link to="/price-lists">Price lists</Link> → upload the supplier&apos;s file (xlsx/csv) or
        paste rows. Parsing runs in the background and stages the rows as a draft version — nothing
        is priced from it yet.
      </>
    ),
  },
  {
    title: "Publish the list",
    body: (
      <>
        Open the parsed list, check the parse summary (row counts per rate card), then hit{" "}
        <strong>Publish</strong>. Only published rows become current prices. Re-uploading the same
        file later creates a new version and applies only what changed.
      </>
    ),
  },
  {
    title: "Create a project and a pricing",
    body: (
      <>
        <Link to="/projects">Projects</Link> → new project → new pricing. A pricing prices{" "}
        <em>your buy list</em> against the published supplier prices.
      </>
    ),
  },
  {
    title: "Add your buy list",
    body: (
      <>
        Paste lines, drag in POs / invoices / order confirmations (xlsx, pdf, msg, eml…), or add
        lines by hand. Lines auto-match to products; anything uncertain is queued for a quick
        review in the line&apos;s match column.
      </>
    ),
  },
  {
    title: "Compute",
    body: (
      <>
        Adjust the filters if needed (brands, suppliers, price age), then <strong>Compute</strong>:
        you get the best single supplier and the cheapest per-line split, side by side, with
        per-line detail.
      </>
    ),
  },
];

export function AboutPage() {
  return (
    <div className="stack">
      <Panel>
        <PanelHead title="What this is" sub="and why it works the way it does" />
        <PanelBody className="stack">
          <div className="sub">
            The app answers one question: for the materials you&apos;re about to buy, which
            supplier — or split of suppliers — gives the lowest total, using the prices you&apos;ve
            actually been quoted (your rate cards), not list prices.
          </div>
          <div className="sub">
            It works by keeping a <strong>versioned price history</strong>: each published price
            list becomes the current nett price per product per rate card, while past prices stay
            queryable — so a pricing computed against last month&apos;s prices can be reproduced.
            Your buy list is matched line-by-line to those products: exact supplier-code matches go
            through automatically, fuzzy ones queue for a quick confirm (AI helps with messy
            documents), and comparables let an equivalent product from another brand stand in
            where you allow it.
          </div>
          <div className="sub">
            The manual checkpoints are deliberate: a comparison is only as trustworthy as the
            matches and prices beneath it. Publishing is explicit so a bad parse never silently
            changes prices, matches are confirmable, and every change is audited.
          </div>
        </PanelBody>
      </Panel>
      <Panel>
        <PanelHead title="How it works" sub="supplier prices in → buy list → computed comparison" />
        <PanelBody className="stack">
          {STEPS.map((step, index) => (
            <div key={step.title} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <Pill tone="neutral">{index + 1}</Pill>
              <div>
                <strong>{step.title}</strong>
                <div className="sub">{step.body}</div>
              </div>
            </div>
          ))}
        </PanelBody>
      </Panel>
      <Panel>
        <PanelHead title="Good to know" />
        <PanelBody className="stack">
          <div className="sub">
            • Prices exist only after a list is <strong>published</strong> — until then every
            pricing reports &quot;No supplier can price this list&quot;.
          </div>
          <div className="sub">
            • A pricing needs at least one buy-list line before Compute can rank suppliers.
          </div>
          <div className="sub">
            • <Link to="/comparables">Comparables</Link> group equivalent products across brands so
            cross-brand alternatives can be priced.
          </div>
          <div className="sub">
            • PDF and email parsing plus match assistance use AI with a hard spend cap — see{" "}
            <Link to="/ai-usage">AI usage</Link>.
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
}
